import type { OrderStatus } from '../../shared/orderStatus';
import { formatDate, relativeTime } from '../utils/time';

interface OrderHeaderProps {
  order: OrderStatus;
}

function globalStatusStyle(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('complete') || s === 'delivered' || s.includes('closed'))
    return 'bg-brand-100 text-brand-700 border-brand-200';
  if (s.includes('ship')) return 'bg-blue-100 text-blue-700 border-blue-200';
  if (s.includes('progress') || s === 'mixed') return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

export function OrderHeader({ order }: OrderHeaderProps) {
  const totalLines = order.lines.filter((l) => l.itemSaleType !== 'Core').length;
  const completedLines = order.lines.filter(
    (l) => l.itemSaleType !== 'Core' && l.steps?.every((s) => s.status === 'completed'),
  ).length;
  const orderedAt = order.orderDateTime || order.orderCreatedAt;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-brand-600 via-brand-500 to-brand-400" />
      <div className="px-5 py-5 sm:px-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1.5 flex-wrap">
              <h1 className="text-xl font-bold tracking-tight">{order.cbmOrderNo}</h1>
              <span
                className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${globalStatusStyle(order.globalStatus)}`}
              >
                {order.globalStatus}
              </span>
            </div>
            {order.sellToCustomerName && (
              <p className="text-sm text-slate-600">{order.sellToCustomerName}</p>
            )}
          </div>

          {orderedAt && (
            <div className="text-right text-xs text-slate-400 space-y-0.5">
              <p>
                Ordered{' '}
                <span className="text-slate-600 font-medium" title={formatDate(orderedAt)}>
                  {relativeTime(orderedAt)}
                </span>
              </p>
              <p className="font-mono text-[10px]">{formatDate(orderedAt)}</p>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center gap-4 flex-wrap text-xs">
          {totalLines > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-brand-500" />
              <span className="text-slate-500">
                {completedLines}/{totalLines} items complete
              </span>
            </div>
          )}
          {!order.isOpen && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-brand-500" />
              <span className="text-slate-500">Order closed</span>
            </div>
          )}
          {order.isOpen && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
              <span className="text-slate-500">In progress</span>
            </div>
          )}
          {order.coreChargeDeferred && (
            <div className="flex items-center gap-1.5" title="Core charge invoiced but payment deferred until inspection">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-slate-500">Deferred core charge</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
